import {
  useGetRenewVisaBasicInformationDocumentGroupInfo,
  useGetRenewVisaDocumentGroupInfo,
  useGetRenewVisaDocumentInfo,
  useGetRenewVisaEmployeeApplicationDocumentInfo,
  useGetRenewVisaUploadFileDocumentInfo,
} from "@/api/services/main/renew-visa";
import { RENEW_VISA_UPLOAD_APPLICATION_FILE, TUploadFileWithCondition } from "@/features/renew-visa/constants/uploadApplicationFile";
import { ECompanyCondition } from "@/features/renew-visa/enums/renewVisaCondition";
import { TRenewVisaUploadFileForm } from "@/features/renew-visa/types/renewVisaUploadFileForm";
import { findRenewVisaDocumentStepGroup } from "@/features/renew-visa/utils/findDocumentStepGroup";
import { DOCUMENT_STEP_KEYS } from "@/types/enum";
import { toNumber } from "@/utils/number";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

export type FileWithFilterCondition = {
  label: string;
  subLabel: string;
  value: (TUploadFileWithCondition & { isUploaded: boolean })[];
};

type UseGetUploadFileListWithFilterConditionReturn = {
  uploadFileListWithFilterCondition: FileWithFilterCondition[];
  filterConditionArrayKeys: (keyof TRenewVisaUploadFileForm)[];
  conditions: string[];
  isLoading: boolean;
};

export const useGetUploadFileListWithFilterCondition = (): UseGetUploadFileListWithFilterConditionReturn => {
  const { documentStepId } = useParams();

  const { data: renewVisaDocumentGroup, isLoading: isLoadingRenewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const { data: renewVisaDocument, isLoading: isLoadingRenewVisaDocument } = useGetRenewVisaDocumentInfo();

  const basicInfoStepGroupData = findRenewVisaDocumentStepGroup(renewVisaDocumentGroup, DOCUMENT_STEP_KEYS.RENEW_VISA_GROUP_BASIC_INFORMATION);

  const { data: basicInfoGroupData, isLoading: isLoadingBasicInfoGroup } = useGetRenewVisaBasicInformationDocumentGroupInfo({
    renewVisaDocumentStepGroupId: basicInfoStepGroupData?.id ?? "",
  });

  const { data: uploadFileData, isLoading: isLoadingUploadFile } = useGetRenewVisaUploadFileDocumentInfo({
    documentStepId: toNumber(documentStepId),
    enabled: true,
  });

  const { data: employeeApplicationData, isLoading: isLoadingEmployeeApplication } = useGetRenewVisaEmployeeApplicationDocumentInfo();

  const conditions = useMemo(() => (basicInfoGroupData?.conditions ?? []) as ECompanyCondition[], [basicInfoGroupData, renewVisaDocumentGroup]);

  const employeeCompanySpecificIndustryName = useMemo(
    () =>
      employeeApplicationData?.contractSpecificIndustry?.companySpecificIndustry?.companySpecificIndustry ||
      renewVisaDocument?.employee.contractSpecificBusinessClassificationOne?.companySpecificBusinessClassification.companySpecificIndustry
        ?.companySpecificIndustry,

    [employeeApplicationData, renewVisaDocument],
  );

  const companyDivision = useMemo(() => renewVisaDocumentGroup?.company.division ?? null, [renewVisaDocumentGroup]);

  const uploadFileListWithFilterCondition = useMemo(() => {
    const array = Object.values(RENEW_VISA_UPLOAD_APPLICATION_FILE).map((group) => {
      const returnItem = {
        label: group.label,
        subLabel: group.subLabel,
        value: group.value
          .filter((detailFile) => (Array.isArray(detailFile.condition) && detailFile.condition.length === 0 ? conditions.length === 0 : true))
          .filter((detailFile) =>
            Array.isArray(detailFile.condition) && detailFile.condition.length > 0
              ? detailFile.condition.some((condition) => conditions.includes(condition))
              : true,
          )
          .filter((detailFile) =>
            typeof detailFile.condition === "string" && detailFile.condition !== ECompanyCondition.ALL
              ? conditions.includes(detailFile.condition)
              : true,
          )
          .filter((detailFile) =>
            detailFile.companySpecificIndustryIdsCondition
              ? detailFile.companySpecificIndustryIdsCondition.some((item) => employeeCompanySpecificIndustryName === item)
              : true,
          )
          .filter((detailFile) => (companyDivision ? detailFile.companyDivisionCondition?.includes(companyDivision) : true))
          .filter((item) => {
            const isUploaded = uploadFileData ? !!uploadFileData[item.key] || (!!item.isExistKey && !!uploadFileData[item.isExistKey]) : false;
            return item.onlyShowWhenHasFile === true && !isUploaded ? false : true;
          })
          .map((item) => ({
            ...item,
            isUploaded: uploadFileData
              ? item.children
                ? item.children.some((childItem) => !!uploadFileData[childItem.key])
                : !!uploadFileData[item.key] || (!!item.isExistKey && !!uploadFileData[item.isExistKey])
              : false,
          }))
          .map((item) => ({
            ...item,
            children: item.children?.filter((childItem) => {
              const isUploaded = uploadFileData ? !!uploadFileData[childItem.key] : false;
              return item.onlyShowWhenHasFile === true && !isUploaded ? false : true;
            }),
          })),
      };
      return returnItem;
    });
    return array;
  }, [conditions, uploadFileData, companyDivision, employeeCompanySpecificIndustryName]);

  const filterConditionArrayKeys = useMemo(
    () =>
      uploadFileListWithFilterCondition.flatMap((item) =>
        item.value.flatMap((valueItem) => (!!valueItem.children ? valueItem.children.map((item) => item.key) : [valueItem.key])),
      ),
    [uploadFileListWithFilterCondition],
  );

  const isLoading = useMemo(
    () =>
      isLoadingRenewVisaDocumentGroup || isLoadingBasicInfoGroup || isLoadingUploadFile || isLoadingEmployeeApplication || isLoadingRenewVisaDocument,
    [isLoadingRenewVisaDocumentGroup, isLoadingBasicInfoGroup, isLoadingUploadFile, isLoadingEmployeeApplication, isLoadingRenewVisaDocument],
  );

  return { isLoading, uploadFileListWithFilterCondition, filterConditionArrayKeys, conditions };
};
